import { Box, Skeleton, Typography, alpha, useTheme } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCurrency } from "../../../Funcoes/formatCurrency";
import { formatNumber } from "../../../Funcoes/formatNumber";

const STATUS_COLORS = {
  PENDENTE: "#b42318",
  "DENTRO DO PRAZO": "#64748b",
  "ENTREGA PARCIAL": "#b45309",
  ENTREGUE: "#2e7d52",
};

const ChartShell = ({ title, subtitle, children, isDark }) => (
  <Box
    sx={{
      border: "1px solid var(--ctrl-border)",
      borderRadius: "14px",
      bgcolor: "var(--ctrl-surface)",
      p: 2.25,
      minHeight: 320,
      display: "flex",
      flexDirection: "column",
      boxShadow: isDark
        ? "0 8px 24px rgba(0,0,0,0.22)"
        : "0 8px 22px rgba(27,58,75,0.06)",
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: 15,
        color: "var(--ctrl-text)",
        mb: 0.5,
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: 12.5,
        color: "var(--ctrl-text-muted)",
        mb: 2,
      }}
    >
      {subtitle}
    </Typography>
    <Box sx={{ flex: 1, minHeight: 240 }}>{children}</Box>
  </Box>
);

const EntregaStatusChart = ({ entrega, loading }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const muted = isDark ? "#94a3b8" : "#64748b";
  const grid = isDark ? "rgba(148,163,184,0.16)" : "rgba(100,116,139,0.18)";
  const tooltipBg = isDark ? "#1e293b" : "#ffffff";
  const tooltipBorder = isDark ? "#334155" : "#e2e8f0";

  const data = (Array.isArray(entrega) ? entrega : []).map((row) => ({
    status: row.status,
    label: row.status,
    quantidade: Number(row.quantidade) || 0,
    valor: Number(row.valor) || 0,
  }));

  const hasData = data.some((row) => row.quantidade > 0);

  if (loading) {
    return (
      <ChartShell
        title="Status de entrega"
        subtitle="Itens de PC no período por situação"
        isDark={isDark}
      >
        <Skeleton
          variant="rounded"
          height="100%"
          sx={{ bgcolor: alpha("#E8A317", 0.1), minHeight: 220 }}
        />
      </ChartShell>
    );
  }

  return (
    <ChartShell
      title="Status de entrega"
      subtitle="Itens de PC no período por situação"
      isDark={isDark}
    >
      {!hasData ? (
        <Box
          sx={{
            height: "100%",
            minHeight: 220,
            display: "grid",
            placeItems: "center",
            color: "var(--ctrl-text-muted)",
            fontSize: 13,
          }}
        >
          Sem dados para exibir
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: muted, fontSize: 10 }}
              axisLine={{ stroke: grid }}
              tickLine={false}
              interval={0}
              angle={-12}
              textAnchor="end"
              height={48}
            />
            <YAxis
              tick={{ fill: muted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={40}
              allowDecimals={false}
            />
            <Tooltip
              formatter={(value, _name, item) => [
                `${formatNumber(value)} itens · R$ ${formatCurrency(
                  item?.payload?.valor
                )}`,
                "Total",
              ]}
              labelFormatter={(label) => label}
              contentStyle={{
                background: tooltipBg,
                border: `1px solid ${tooltipBorder}`,
                borderRadius: 10,
                color: isDark ? "#f1f5f9" : "#0f172a",
                fontSize: 12,
              }}
              cursor={{ fill: alpha("#E8A317", isDark ? 0.08 : 0.1) }}
            />
            <Bar dataKey="quantidade" radius={[6, 6, 0, 0]} maxBarSize={48}>
              {data.map((entry) => (
                <Cell
                  key={entry.status}
                  fill={STATUS_COLORS[entry.status] || "#E8A317"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartShell>
  );
};

export default EntregaStatusChart;
